import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {FetchStatus} from '../../../../../../common/FetchStatus.enum';
import {ViewerGuideType} from '../../../../../../design-system/organisms/ViewerGuide/ViewerGuideType.enum';
import {TicketInfo} from '../../../../../../graphql/generated/types';
import {VIEWER_INFO_STATE_NAME} from '../constant/viewerConstant';
import {fetchShortVodViewerPreviewInfo} from '../util/fetchShortVodViewerPreviewInfo.thunk';
import {fetchTextViewerPreviewInfo} from '../util/fetchTextViewerPreviewInfo.thunk';
import {ViewerInfoState} from './ViewerInfoState.interface';

export const viewerInfoSlice = createSlice({
  name: VIEWER_INFO_STATE_NAME,
  initialState: {
    isFromPrevProduct: false,
    nonstopWatchingInSeries: {},
    textViewerPreview: {},
    shortVodViewerPreview: {},
    viewerContinued: 0,
    isViewerPreviewCommentOn: true,
    hideAgeGradeLabel: false,
    ticketFlowInProgress: false,
    isEpisodeChanging: false,
    canPullToRefresh: true,
  } as ViewerInfoState,
  reducers: {
    setCurrentViewerGuide: (
      state,
      action: PayloadAction<ViewerGuideType | undefined>
    ) => {
      state.prevViewerGuide = state.currentViewerGuide;
      state.currentViewerGuide = action.payload;
    },
    resetViewerGuide: state => {
      state.prevViewerGuide = undefined;
      state.currentViewerGuide = undefined;
    },
    setNonstopWatching: (
      state,
      {
        payload: {seriesId, isNonstopWatching, selectedBuyTicketInfo},
      }: PayloadAction<{
        seriesId: number;
        isNonstopWatching: boolean;
        selectedBuyTicketInfo?: TicketInfo;
      }>
    ) => {
      state.nonstopWatchingInSeries[seriesId] = {
        isNonstopWatching,
        selectedBuyTicketInfo,
      };
    },
    setNonstopWatchingSelectedBuyTicketInfo: (
      state,
      {
        payload: {seriesId, selectedBuyTicketInfo},
      }: PayloadAction<{
        seriesId: number;
        selectedBuyTicketInfo?: TicketInfo;
      }>
    ) => {
      if (!state.nonstopWatchingInSeries[seriesId]) {
        state.nonstopWatchingInSeries[seriesId] = {isNonstopWatching: true};
      }
      state.nonstopWatchingInSeries[seriesId].selectedBuyTicketInfo =
        selectedBuyTicketInfo;
    },
    setFromPrevProduct: (state, action: PayloadAction<boolean>) => {
      state.isFromPrevProduct = action.payload;
    },
    clearFromPrevProduct: state => {
      state.isFromPrevProduct = false;
    },
    setIsViewerToolTipExposed: (state, action: PayloadAction<boolean>) => {
      state.isToolTipExposed = action.payload;
    },
    setIsViewerPreviewCommentOn: (state, action: PayloadAction<boolean>) => {
      state.isViewerPreviewCommentOn = action.payload;
    },
    setCurrentViewerIds: (
      state,
      action: PayloadAction<{
        currentProductId: number;
        currentSeriesId: number;
      }>
    ) => {
      const {currentProductId, currentSeriesId} = action.payload;
      state.currentProductId = currentProductId;
      state.currentSeriesId = currentSeriesId;
    },
    clearCurrentViewerIds: state => {
      state.currentProductId = undefined;
      state.currentSeriesId = undefined;
    },
    setViewerContinued: (
      state,
      action: PayloadAction<{
        viewerContinued: number;
      }>
    ) => {
      const {viewerContinued} = action.payload;
      state.viewerContinued = viewerContinued;
    },
    setHideAgeGradeLabel: (state, action: PayloadAction<boolean>) => {
      state.hideAgeGradeLabel = action.payload;
    },
    setTicketFlowInProgress: (state, action: PayloadAction<boolean>) => {
      state.ticketFlowInProgress = action.payload;
    },
    setIsEpisodeChanging: (state, action: PayloadAction<boolean>) => {
      state.isEpisodeChanging = action.payload;
    },
    setCanPullToRefresh: (state, action: PayloadAction<boolean>) => {
      state.canPullToRefresh = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchTextViewerPreviewInfo.pending, (state, action) => {
      if (!state.textViewerPreview[action.meta.arg.previewKey]) {
        state.textViewerPreview[action.meta.arg.previewKey] = {
          fetchStatus: FetchStatus.Idle,
        };
      }
      state.textViewerPreview[action.meta.arg.previewKey].fetchStatus =
        FetchStatus.Loading;
    });
    builder.addCase(
      fetchTextViewerPreviewInfo.fulfilled,
      (state, {meta, payload}) => {
        if (!state.textViewerPreview[meta.arg.previewKey]) {
          state.textViewerPreview[meta.arg.previewKey] = {
            fetchStatus: FetchStatus.Idle,
          };
        }
        if (!payload) {
          state.textViewerPreview[meta.arg.previewKey].fetchStatus =
            FetchStatus.Error;
          return;
        }
        state.textViewerPreview[meta.arg.previewKey].fetchStatus =
          FetchStatus.Success;
        state.textViewerPreview[meta.arg.previewKey].data = payload;
      }
    );
    builder.addCase(fetchTextViewerPreviewInfo.rejected, (state, {meta}) => {
      if (!state.textViewerPreview[meta.arg.previewKey]) {
        state.textViewerPreview[meta.arg.previewKey] = {
          fetchStatus: FetchStatus.Idle,
        };
      }
      state.textViewerPreview[meta.arg.previewKey].fetchStatus =
        FetchStatus.Error;
    });

    builder.addCase(fetchShortVodViewerPreviewInfo.pending, (state, action) => {
      if (!state.shortVodViewerPreview[action.meta.arg.previewKey]) {
        state.shortVodViewerPreview[action.meta.arg.previewKey] = {
          fetchStatus: FetchStatus.Idle,
        };
      }
      state.shortVodViewerPreview[action.meta.arg.previewKey].fetchStatus =
        FetchStatus.Loading;
    });
    builder.addCase(
      fetchShortVodViewerPreviewInfo.fulfilled,
      (state, {meta, payload}) => {
        if (!state.shortVodViewerPreview[meta.arg.previewKey]) {
          state.shortVodViewerPreview[meta.arg.previewKey] = {
            fetchStatus: FetchStatus.Idle,
          };
        }
        if (!payload) {
          state.shortVodViewerPreview[meta.arg.previewKey].fetchStatus =
            FetchStatus.Error;
          return;
        }
        state.shortVodViewerPreview[meta.arg.previewKey].fetchStatus =
          FetchStatus.Success;
        state.shortVodViewerPreview[meta.arg.previewKey].data = payload;
      }
    );
    builder.addCase(
      fetchShortVodViewerPreviewInfo.rejected,
      (state, {meta}) => {
        if (!state.shortVodViewerPreview[meta.arg.previewKey]) {
          state.shortVodViewerPreview[meta.arg.previewKey] = {
            fetchStatus: FetchStatus.Idle,
          };
        }
        state.shortVodViewerPreview[meta.arg.previewKey].fetchStatus =
          FetchStatus.Error;
      }
    );
  },
});

export const {
  setNonstopWatching,
  setNonstopWatchingSelectedBuyTicketInfo,
  setFromPrevProduct,
  setCurrentViewerGuide,
  resetViewerGuide,
  setIsViewerToolTipExposed,
  setIsViewerPreviewCommentOn,
  setCurrentViewerIds,
  clearCurrentViewerIds,
  setViewerContinued,
  clearFromPrevProduct,
  setHideAgeGradeLabel,
  setTicketFlowInProgress,
  setIsEpisodeChanging,
  setCanPullToRefresh,
} = viewerInfoSlice.actions;
